// /src/components/layout/Layout/MainLayout.jsx

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Box, useTheme, useMediaQuery } from '@mui/material';
import { styled } from '@mui/material/styles';

// Layout Components
import AppBarComponent from '../AppBar/AppBarComponent';
import MainDrawer from '../Drawer/MainDrawer';
import DynamicBreadcrumbs from '../Breadcrumbs/DynamicBreadcrumbs';

// Config
import { getLayoutConfig } from '../../../config/routeConfig';
import { useAuth } from '../../../context/AuthContext';
import UpdateAlert from '../Alert/UpdateAlert';

const DRAWER_WIDTH = 280;
const APPBAR_HEIGHT = 53;

const MainContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'open' && prop !== 'isMobile',
})(() => ({
  display: 'flex',
  height: '100vh',
}));

const ContentContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'open' && prop !== 'isMobile' && prop !== 'transparent',
})(({ theme, open, isMobile, transparent }) => ({
  flexGrow: 1,
  backgroundColor: transparent ? 'transparent' : theme.palette.background.default,
  marginLeft: isMobile ? 0 : (open ? DRAWER_WIDTH : 0),
  marginTop: APPBAR_HEIGHT,
  transition: theme.transitions.create(['margin'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && !isMobile && {
    transition: theme.transitions.create(['margin'], {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
  // Center the content only when not full width
  display: 'flex',
  justifyContent: 'center',
  overflowY: 'auto',
  // Explicit touch-scroll hints — without these, nested (non-body) scroll
  // containers can fail to respond to swipe on touchscreen kiosk browsers
  // (Raspberry Pi), since the browser has no native <body> scroll to fall
  // back on and default touch handling on a div is unreliable.
  WebkitOverflowScrolling: 'touch',
  touchAction: 'pan-y',
  overscrollBehavior: 'contain',
  width: isMobile ? '100vw' : (open ? `calc(100vw - ${DRAWER_WIDTH}px)` : '100vw'),
}));


export default function MainLayout() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { user } = useAuth();
  const location = useLocation();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Get layout configuration for current route
  const layoutConfig = useMemo(() => getLayoutConfig(location.pathname), [location.pathname]);

  // Memoize initial drawer state to prevent unnecessary re-renders
  const initialDrawerOpen = useMemo(() => !isMobile, [isMobile]);
  const [drawerOpen, setDrawerOpen] = useState(initialDrawerOpen);
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);

  // Handle responsive drawer behavior
  useEffect(() => {
    if (isMobile) {
      setDrawerOpen(false);
    } else {
      setMobileDrawerOpen(false);
      setDrawerOpen(true);
    }
  }, [isMobile]);

  useEffect(() => {
    if (user.force_password_change) {
      navigate("/force-password")
    } else if (!user.color) {
      navigate("/force-color")
    } else if (user.show_pin_setup_prompt) {
      navigate("/force-pin")
    }
  }, [navigate, user])

  // Memoize event handlers to prevent recreation on every render
  const handleDrawerToggle = useCallback(() => {
    if (isMobile) {
      setMobileDrawerOpen(prev => !prev);
    } else {
      setDrawerOpen(prev => !prev);
    }
  }, [isMobile]);

  const handleMobileDrawerClose = useCallback(() => {
    setMobileDrawerOpen(false);
  }, []);

  // Memoize computed values
  const isDrawerOpen = !isMobile && drawerOpen;

  return (
    <MainContainer>
      {/* App Bar */}
      <AppBarComponent
        onMenuClick={handleDrawerToggle}
        drawerWidth={DRAWER_WIDTH}
        drawerOpen={isDrawerOpen}
      />

      {/* Navigation Drawer */}
      <MainDrawer
        width={DRAWER_WIDTH}
        open={drawerOpen}
        mobileOpen={mobileDrawerOpen}
        onMobileClose={handleMobileDrawerClose}
        isMobile={isMobile}
      />

      {/* Main Content Area */}
      <ContentContainer
        component="main"
        open={drawerOpen}
        isMobile={isMobile}
        transparent={!!layoutConfig.transparentBg}
      >
        <Box sx={[
          {
            p: 3,
            width: '100%',
            height: '100%',
            boxSizing: 'border-box',
            position: 'relative',
            maxWidth: '1200px',
            margin: '0 auto',
            [theme.breakpoints.down('sm')]: { p: 2, px: 1 },
            display: 'flex',
            flexDirection: 'column',
          },
          layoutConfig.sx,
        ]}>
          {/* Update alert */}
          <UpdateAlert />

          {/* Breadcrumbs */}
          {!layoutConfig.hideBreadcrumbs && <DynamicBreadcrumbs />}

          {/* Page Content */}
          <Outlet />
        </Box>
      </ContentContainer>
    </MainContainer>
  );
}